/** Partner portal — `salescrmapi/src/modules/partnerPortal` (mounted at /v1/partner). */
import axios from 'axios';
import apiClient, { idempotencyKey } from './axiosClient.js';

const clean = (p = {}) => Object.fromEntries(Object.entries(p).filter(([, v]) => v !== '' && v != null));
const get = async (url, params) => (await apiClient.get(`/partner${url}`, { params: clean(params) })).data;
const post = async (url, body, idem = false) => (await apiClient.post(`/partner${url}`, body, idem ? { headers: idempotencyKey() } : undefined)).data;
const put = async (url, body, headers) => (await apiClient.put(`/partner${url}`, body, headers ? { headers } : undefined)).data;
const patch = async (url, body) => (await apiClient.patch(`/partner${url}`, body)).data;

/* auth / profile */
export const activate = (body) => post('/auth/activate', body);
export const me = () => get('/auth/me');

/* CRM admin */
export const createPartner = (b) => post('/admin/partners', b, true);
export const salesManagers = () => get('/admin/sales-managers');
export const listPartners = (p) => get('/admin/partners', p);
export const getPartner = (id) => get(`/admin/partners/${id}`);
export const updatePartner = (id, b) => put(`/admin/partners/${id}`, b);
export const setPartnerStatus = (id, status) => patch(`/admin/partners/${id}/status`, { status });
export const sendPartnerPasswordReset = (id, puId) => post(`/admin/partners/${id}/users/${puId}/reset-password`, {});
export const partnerUsers = (id) => get(`/admin/partners/${id}/users`).then((r) => r.data);

/* company team */
export const listTeam = () => get('/team/users').then((r) => r.data);
export const addTeamUser = (b) => post('/team/users', b, true);
export const updateTeamUser = (id, b) => put(`/team/users/${id}`, b);
export const setTeamUserStatus = (id, status) => patch(`/team/users/${id}/status`, { status });
export const resendInvitation = (id) => post(`/team/users/${id}/resend-invitation`, {});

/* leads */
export const listLeads = (p) => get('/leads', p);
export const getLead = (id) => get(`/leads/${id}`);
export const submitLead = (b) => post('/leads', b, true);
export const updateLead = (id, b, rowVersion) => put(`/leads/${id}`, b, { 'If-Match': `"${rowVersion}"` });
export const submitDraft = (id) => post(`/leads/${id}/submit`, {});
export const duplicateCheck = (p) => get('/leads/duplicate-check', p);
export const timeline = (id) => get(`/leads/${id}/timeline`).then((r) => r.data);
export const assignToMember = (id, b, change) => (change ? put(`/leads/${id}/team-assignment`, b) : post(`/leads/${id}/team-assignment`, b)).then((r) => r.data);
export const assignmentHistory = (id) => get(`/leads/${id}/assignment-history`).then((r) => r.data);

/* comments & communications */
export const comments = (id) => get(`/leads/${id}/comments`).then((r) => r.data);
export const addComment = (id, b) => post(`/leads/${id}/comments`, b, true);
export const replyTo = (id, cid, b) => post(`/leads/${id}/comments/${cid}/replies`, b, true);
export const communications = (id) => get(`/leads/${id}/communications`).then((r) => r.data);
export const addCommunication = (id, b) => post(`/leads/${id}/communications`, b);

/* documents — bytes go straight to blob storage, never through the API */
export const documents = (id) => get(`/leads/${id}/documents`).then((r) => r.data);
export const documentDownloadUrl = (id, docId) => get(`/leads/${id}/documents/${docId}/download-url`);
async function sha256Hex(file) {
  const buf = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
export async function uploadDocument(leadId, file, visibility) {
  const contentType = file.type || 'application/octet-stream';
  const reserved = await post(`/leads/${leadId}/documents/upload-url`, {
    filename: file.name, content_type: contentType, byte_size: file.size, checksum_sha256: await sha256Hex(file),
  }, true);
  const putRes = await axios.put(reserved.upload.url, file, { headers: reserved.upload.headers });
  return post(`/leads/${leadId}/documents`, { object_id: reserved.object_id, etag: putRes.headers.etag ?? putRes.headers.ETag ?? 'unknown', ...(visibility ? { visibility } : {}) });
}

/* review (sales manager) */
export const reviewQueue = (p) => get('/review/leads', p);
export const reviewSalesReps = () => get('/review/sales-reps');
export const reviewDetail = (id) => get(`/review/leads/${id}`);
export const approveLead = (id, b) => post(`/review/leads/${id}/approve`, b, true);
export const rejectLead = (id, b) => post(`/review/leads/${id}/reject`, b);
export const requestInformation = (id, b) => post(`/review/leads/${id}/request-information`, b);
export const assignSalesRep = (id, b) => post(`/review/leads/${id}/assign-sales-rep`, b);

/* deals, dashboards, notifications */
export const listDeals = (p) => get('/deals', p);
export const dashboardSummary = () => get('/dashboard/summary');
export const leadStatistics = () => get('/dashboard/lead-statistics');
export const monthlyTrends = () => get('/dashboard/monthly-trends').then((r) => r.data);
export const recentActivities = () => get('/dashboard/recent-activities');
export const teamPerformance = () => get('/dashboard/team-performance');
export const notifications = (p) => get('/notifications', p);
export const markRead = (id) => apiClient.patch(`/partner/notifications/${id}/read`);
export const markAllRead = () => apiClient.post('/partner/notifications/read-all');
export const listProjects = (p) => get('/projects', p);
export const getProject = (id) => get(`/projects/${id}`);
